import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, RefreshCw, FileText, ExternalLink, AlertTriangle } from 'lucide-react';
import Pagination from './Pagination';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPLETION_FILTERS = [
  { key: 'all', label: 'All sessions' },
  { key: 'marked_complete', label: 'Marked complete' },
  { key: 'not_marked_complete', label: 'Analysis done — not marked complete' },
  { key: 'analysis_incomplete', label: 'Analysis not completed by client' },
  { key: 'multiple_analyses', label: 'Analysis done more than once' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return '—'; }
};

/**
 * ReportsLibrary
 * Reads all sessions the current psychologist has ever worked on and shows
 * a searchable table of clients. Expired sessions ARE included and annotated.
 */
const ReportsLibrary = ({ onError }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [completionFilter, setCompletionFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/psychologist/reports-library`);
      setRows(res.data?.reports || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load Analysis library';
      if (onError) onError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows;
    if (completionFilter === 'multiple_analyses') {
      out = out.filter((r) => r.assessment_done_multiple_times);
    } else if (completionFilter !== 'all') {
      out = out.filter((r) => r.completion_status === completionFilter);
    }
    if (needle) {
      out = out.filter((r) =>
        (r.client_name || '').toLowerCase().includes(needle) ||
        (r.client_email || '').toLowerCase().includes(needle) ||
        (r.session_id || '').toLowerCase().includes(needle)
      );
    }
    return out;
  }, [rows, q, completionFilter]);

  // Reset to page 1 whenever the filter, search, or dataset changes so the
  // therapist never lands on an out-of-range page.
  useEffect(() => { setCurrentPage(1); }, [q, completionFilter, rows.length]);

  const paged = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  // Counts per bucket for the dropdown label (small UX polish).
  const bucketCounts = useMemo(() => {
    const c = {
      all: rows.length,
      marked_complete: 0,
      not_marked_complete: 0,
      analysis_incomplete: 0,
      multiple_analyses: 0,
    };
    for (const r of rows) {
      if (r.completion_status && c[r.completion_status] !== undefined) c[r.completion_status] += 1;
      if (r.assessment_done_multiple_times) c.multiple_analyses += 1;
    }
    return c;
  }, [rows]);

  const openSession = (row) => {
    if (!row.sqis?.generated && !row.all_four_phases_completed) return;
    navigate(`/session/${row.session_id}?viewReportsOnly=true`);
  };

  return (
    <div className="space-y-4" data-testid="reports-library-root">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2" data-testid="reports-library-title">
            <FileText className="w-5 h-5 text-indigo-600" /> Analysis Library
          </h2>
          <p className="text-sm text-gray-600">
            Every client session you have worked on — visible <b>regardless</b> of analysis
            validity or expiry.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={completionFilter}
            onChange={(e) => setCompletionFilter(e.target.value)}
            className="pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="reports-library-completion-filter"
          >
            {COMPLETION_FILTERS.map((f) => (
              <option key={f.key} value={f.key} data-testid={`filter-opt-${f.key}`}>
                {f.label} ({bucketCounts[f.key] ?? 0})
              </option>
            ))}
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-2 top-2.5" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search client, email, or session id…"
              className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-72 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="reports-library-search"
            />
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            data-testid="reports-library-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center" data-testid="reports-library-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Analysis…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg" data-testid="reports-library-empty">
          No client Analysis found for your account yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm" data-testid="reports-library-table">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Client</th>
                <th className="px-4 py-2 text-left font-semibold">Session Date</th>
                <th className="px-4 py-2 text-left font-semibold">SQIS 14 Points</th>
                <th className="px-4 py-2 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {paged.map((r) => (
                <tr key={r.session_id} data-testid={`reports-row-${r.session_id}`}>
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium text-gray-900">{r.client_name}</div>
                    <div className="text-xs text-gray-500">{r.client_email || '—'}</div>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {r.is_expired && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                          <AlertTriangle className="w-3 h-3" /> Expired analysis
                        </span>
                      )}
                      {r.assessment_done_multiple_times && (
                        <span
                          className="inline-flex items-center text-[11px] text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded"
                          title={`${r.client_completed_count} completed analyses on record for this client`}
                          data-testid={`badge-multiple-analyses-${r.session_id}`}
                        >
                          ×{r.client_completed_count} analyses
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-gray-700">
                    <div>{formatDate(r.scheduled_at || r.created_at)}</div>
                    <div className="text-[11px] text-gray-500">Status: {r.session_status || '—'}</div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {r.sqis?.generated ? (
                      <>
                        <div className="text-gray-900 font-medium">{r.sqis.point_count} points</div>
                        <div className="text-[11px] text-gray-600">
                          Generated: {formatDate(r.sqis.last_updated_at)}
                        </div>
                        <div className="text-[11px] text-gray-600">
                          Reviewed: {r.sqis.reviewed ? formatDate(r.sqis.reviewed_at) : 'Not yet'}
                        </div>
                        <div className={`text-[11px] font-medium ${r.sqis.reviewed ? 'text-emerald-700' : 'text-indigo-700'}`}>
                          {r.sqis.reviewed ? 'Reviewed' : 'Generated – Not Reviewed'}
                        </div>
                        {r.sqis.psychologist_name && (
                          <div className="text-[11px] text-gray-500">By: {r.sqis.psychologist_name}</div>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500">Not generated</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {(r.sqis?.generated || r.all_four_phases_completed) ? <div className="flex">
                      <button
                        type="button"
                        onClick={() => openSession(r)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-50"
                        data-testid={`btn-open-session-${r.session_id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Open
                      </button>
                    </div> : <div data-testid={`waiting-for-client-${r.session_id}`}>
                      <span className="inline-flex text-[11px] text-gray-700 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">Waiting for client</span>
                      <div className="mt-1 text-xs text-gray-600">Client has not completed all 4 phases yet</div>
                    </div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
          />
        </div>
      )}

    </div>
  );
};

export default ReportsLibrary;
